import { TFunction } from "i18next";

import { UseProvidedAuthInterface } from "../../../../hooks";
import { Role } from "../../../../models/Shared/Role";
import { User } from "../../../../models/User/User";
import { schoolCandidateInstitutionSummaryListRoute } from "../../../../routes/routes";
import { ADMIN_ROLES, INSTITUTION_ROLES, COUNTY_ROLES } from "./constants";

export const isAllowedToCreateNote = (auth: UseProvidedAuthInterface) => {
  if (!auth.currentRole) return false;

  const isCountyInstitutionRoles = getCountyInstitutionRoles();

  const isAllowedUser = isCountyInstitutionRoles.includes(
    auth.currentRole.role
  );
  const isAdminUser = ADMIN_ROLES.includes(auth.currentRole.role);

  return isAllowedUser || isAdminUser;
};

export const canEditNote = (
  auth: UseProvidedAuthInterface,
  creatorId: User["id"],
  role: Role
) => {
  if (!auth.currentRole || !auth.user) return false;

  // Creator can always edit their own notes
  if (creatorId === auth.currentRole.id) return true;

  // Admin roles can do everything
  if (ADMIN_ROLES.includes(auth.currentRole.role)) return true;

  // Check if roles are in the same category
  const isCountyRole = COUNTY_ROLES.includes(role);
  const isInstitutionRole = INSTITUTION_ROLES.includes(role);

  const userHasCountyRole = COUNTY_ROLES.includes(auth.currentRole.role);
  const userHasInstitutionRole = INSTITUTION_ROLES.includes(
    auth.currentRole.role
  );

  // Allow if both roles are from the same category
  return (
    (isCountyRole && userHasCountyRole) ||
    (isInstitutionRole && userHasInstitutionRole)
  );
};

export const canDeleteNote = (
  auth: UseProvidedAuthInterface,
  creatorId: User["id"],
  role: Role
) => {
  return canEditNote(auth, creatorId, role);
};

function getCountyInstitutionRoles() {
  const isInstitutionRoute = window.location.pathname.includes(
    schoolCandidateInstitutionSummaryListRoute
  );

  return isInstitutionRoute ? INSTITUTION_ROLES : COUNTY_ROLES;
}

export function getNoteSource(
  t: TFunction,
  creatorRole: Role
): string | undefined {
  if (!creatorRole) return;

  const baseText = t("schoolAttendance.noteSource");

  let sourceText;
  // Normal users will have only one role
  if (ADMIN_ROLES.includes(creatorRole)) {
    sourceText = t("role.AdminRoles");
  } else if (INSTITUTION_ROLES.includes(creatorRole)) {
    sourceText = t("common.institution");
  } else if (COUNTY_ROLES.includes(creatorRole)) {
    sourceText = t("common.county");
  }

  return `${baseText} ${sourceText}`;
}
